import { Injectable } from "@angular/core";

const CURRENT_STEP_KEY = "yooga_current_step";
const LAST_STEP_CONCLUDED_KEY = "yooga_last_step_concluded";
const ONBOARDING_COMPLETED_KEY = "yooga_onboarding_completed";

@Injectable({
  providedIn: "root",
})
export class StepControlService {
  /**
   * Obtém o step atual do localStorage
   * @returns número do step atual ou 0 se não existir
   */
  getCurrentStep(): number {
    try {
      const value = localStorage.getItem(CURRENT_STEP_KEY);
      return value ? parseInt(value, 10) : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Define o step atual no localStorage
   * @param step número do step
   */
  setCurrentStep(step: number): void {
    try {
      localStorage.setItem(CURRENT_STEP_KEY, step.toString());
    } catch {
      console.error("Erro ao salvar current_step no localStorage");
    }
  }

  /**
   * Obtém o último step concluído do localStorage
   * @returns número do último step concluído ou -1 se nenhum foi concluído
   */
  getLastStepConcluded(): number {
    try {
      const value = localStorage.getItem(LAST_STEP_CONCLUDED_KEY);
      return value ? parseInt(value, 10) : -1;
    } catch {
      return -1;
    }
  }

  /**
   * Define o último step concluído no localStorage
   * @param step número do step
   */
  setLastStepConcluded(step: number): void {
    try {
      localStorage.setItem(LAST_STEP_CONCLUDED_KEY, step.toString());
    } catch {
      console.error("Erro ao salvar last_step_concluded no localStorage");
    }
  }

  /**
   * Verifica se o usuário pode acessar um determinado step
   * Um step é acessível se o último step concluído for >= step anterior
   * @param stepIndex índice do step que se deseja acessar
   * @returns true se pode acessar, false caso contrário
   */
  canAccessStep(stepIndex: number): boolean {
    if (stepIndex === 0) {
      return true;
    }

    const lastConcluded = this.getLastStepConcluded();
    return lastConcluded >= stepIndex - 1;
  }

  /**
   * Marca um step como concluído
   * Atualiza o last_step_concluded se o step atual for maior
   * @param stepIndex índice do step concluído
   */
  markStepAsCompleted(stepIndex: number): void {
    const lastConcluded = this.getLastStepConcluded();
    if (stepIndex > lastConcluded) {
      this.setLastStepConcluded(stepIndex);
    }
  }

  /**
   * Avança para o próximo step
   * Marca o step atual como concluído e avança
   * @param currentStepIndex índice do step atual
   */
  advanceToNextStep(currentStepIndex: number): void {
    this.markStepAsCompleted(currentStepIndex);
    this.setCurrentStep(currentStepIndex + 1);
  }

  /**
   * Verifica se o onboarding foi completamente concluído
   */
  isOnboardingCompleted(): boolean {
    try {
      return localStorage.getItem(ONBOARDING_COMPLETED_KEY) === "true";
    } catch {
      return false;
    }
  }

  /**
   * Marca o onboarding como completamente concluído
   */
  markOnboardingAsCompleted(): void {
    try {
      localStorage.setItem(ONBOARDING_COMPLETED_KEY, "true");
    } catch {
      console.error("Erro ao marcar onboarding como concluído");
    }
  }

  /**
   * Reseta todo o controle de steps (útil para testes)
   */
  resetStepControl(): void {
    try {
      localStorage.removeItem(CURRENT_STEP_KEY);
      localStorage.removeItem(LAST_STEP_CONCLUDED_KEY);
      localStorage.removeItem(ONBOARDING_COMPLETED_KEY);
    } catch {
      console.error("Erro ao resetar controle de steps");
    }
  }

  /**
   * Obtém informações de debug sobre o estado atual
   */
  getDebugInfo() {
    return {
      currentStep: this.getCurrentStep(),
      lastStepConcluded: this.getLastStepConcluded(),
      onboardingCompleted: this.isOnboardingCompleted(),
    };
  }
}
